import { Component, OnInit, OnChanges, SimpleChanges, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { FormGroup, ControlContainer } from '@angular/forms';
import { CnafUserService } from '@cnaf/ng-cnaf-commons';
import { Subscription } from 'rxjs';
import { RessourcesModel } from '../ressources/models/ressources.model';
import { SitProForm, SitProModel } from './models/sitpro.model';
import { SitProModelAdapter } from './services/SitProModelAdapter';
import { CcdDateUtils } from '../../../services/ccd-date.utils';
import { TypePersonneEnum } from '../personne/enum/type-personne.enum';
import { AffichageSitProService } from './services/affichage-sitpro.service';
import { AffichageSitProModel } from './models/affichage-sitpro.model';
import { ProprietesQuestionsSitProModel } from './models/proprietes-questions-sitpro.model';
import { LibellesSitProService } from './services/libelles-sitpro.service';

/**
 * Composant utlisé pour afficher les situations professionnelles en fonction des ressources saisies d'une personne
 */
@Component({
  selector: 'app-sitpro',
  templateUrl: './sitpro.component.html',
  styleUrls: ['./sitpro.component.css']
})
export class SitproComponent implements OnInit, OnChanges, OnDestroy {

  @Input() name : string;

  /**
   * ID utilisé pour générer les id uniques dans les éléments du DOM contenus dans le compoosant
   */
  @Input() id = '';

  /**
   * Type de la personne (demandeur ou conjoint)
   */
  @Input() typePersonne = TypePersonneEnum.DEMANDEUR;

  /**
   * Ressources saisies utilisées pour l'affichage des situations professionnelles
   */
  @Input() ressources = new RessourcesModel();

  /**
   * Nombre d'enfants du foyer
   */
  @Input() nbEnfants = 0;

  /**
   * Objet SitProModel renvoyé
   */
  @Output() sitProEvent = new EventEmitter<SitProModel>();

  /**
   * Objet qui donne les libelles des questions / réponses
   */
  libelleQuestions : ProprietesQuestionsSitProModel;

  /**
   * Objet qui contient les données de la situation Pro
   */
  @Input() sitProModel = new SitProModel();

  parentForm : FormGroup;
  
  /**
   * Objet qui determine l'affichage des questions
   */
  affichageQuestions = new AffichageSitProModel();

  /**
   * Liste des subscribers
   */
  subscribers = new Array<Subscription>();

  /**
   * Formulaire du composant
   */
  sitProForm : FormGroup<SitProForm>; 

  codeSituation : string;
  enActivite : string;
  auChomage : string;
  indemnite : string;

  /**
   * Constructeur du composant
   */
  constructor(
    private readonly cnafUserService : CnafUserService, 
    private readonly controlContainer: ControlContainer) {
  }

  /**
   * Init du composant : 
   * - Initialisation du statut de l'affichage des questions
   */
  ngOnInit(): void {
    if (this.id === '') {
      this.id = this.name;
    }
    
    this.sitProForm = new FormGroup<SitProForm>({});

    this.parentForm = this.controlContainer?.control as FormGroup;
    this.parentForm.addControl(this.name, this.sitProForm);

    const dateDuJour : Date = CcdDateUtils.getDateFromAAAAMMJJ(this.cnafUserService.dateEnvCaffr);
    this.libelleQuestions = LibellesSitProService.determinerLibelles(this.typePersonne, this.nbEnfants, dateDuJour);

    this.mapSessionData();

    this.sitProEvent.emit(this.sitProModel);

    this.subscribers.push(
      this.sitProForm.valueChanges.subscribe(dataChange => {
        this.mapperSitProModel(dataChange);
        this.affichageQuestions = AffichageSitProService.determinerAffichage(this.ressources, this.sitProModel);
      })
    );
  }

  /**
   * Donnée en provenant de l'input a mapper vers le formulaire, mise à jour de l'affichage
   */
  mapSessionData() {

    this.codeSituation = this.sitProModel.codeSituation;
    
    if (this.sitProModel.isEnActiviteDefini) {
      this.enActivite = this.sitProModel.enActivite ? "true":"false";
    }
    if (this.sitProModel.isAuChomageDefini) {
      this.auChomage = this.sitProModel.auChomage ? "true":"false";
    }
    if (this.sitProModel.isIndemniteDefini) {
      this.indemnite = this.sitProModel.indemnite ? "true":"false";
    }

    this.affichageQuestions = AffichageSitProService.determinerAffichage(this.ressources, this.sitProModel);
    this.sitProForm.markAsDirty();
  }

  /**
   * Au changement des entrées, on met a jour les questions / réponses (dépendent des ressources et du nombre d'enfant)
   * @param changes SimpleChanges
   */
  ngOnChanges(changes: SimpleChanges) {
      const dateDuJour : Date = CcdDateUtils.getDateFromAAAAMMJJ(this.cnafUserService.dateEnvCaffr);
      this.libelleQuestions = LibellesSitProService.determinerLibelles(this.typePersonne, this.nbEnfants, dateDuJour);
      this.affichageQuestions = AffichageSitProService.determinerAffichage(this.ressources, this.sitProModel);
  }

  /**
   * Destruction du composant
   */
  ngOnDestroy(): void {
    this.subscribers.forEach(sub => sub.unsubscribe());
    this.parentForm.removeControl(this.name);
  }

  /**
   * Mise à jour de l'objet metier de la sitpro en fonction de la saisie du formulaire
   * @param data 
   */
  mapperSitProModel(data) : void {
    this.sitProModel = new SitProModel();
    const sitProAdapter = new SitProModelAdapter(this.sitProModel);
    sitProAdapter.setCodeSituation(this.codeSituation);
    sitProAdapter.setEnActivite(this.enActivite);
    sitProAdapter.setAuChomage(this.auChomage);
    sitProAdapter.setIndeminite(this.indemnite);
    this.sitProEvent.emit(this.sitProModel);
  }
}
